import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';

const FLOW_LEVELS = [
  { id: 'spotting', label: 'Spotting', emoji: '💧' },
  { id: 'light', label: 'Light', emoji: '🌸' },
  { id: 'medium', label: 'Medium', emoji: '🌺' },
  { id: 'heavy', label: 'Heavy', emoji: '💪' }
];

const SYMPTOMS = [
  { id: 'cramps', label: 'Cramps', emoji: '😣' },
  { id: 'headache', label: 'Headache', emoji: '🤕' },
  { id: 'bloating', label: 'Bloating', emoji: '🫄' },
  { id: 'fatigue', label: 'Fatigue', emoji: '😴' },
  { id: 'mood_swings', label: 'Mood Swings', emoji: '😵‍💫' },
  { id: 'breast_tenderness', label: 'Tender', emoji: '💗' },
  { id: 'acne', label: 'Acne', emoji: '🔴' },
  { id: 'back_pain', label: 'Back Pain', emoji: '🔙' }
];

const MOODS = [
  { id: 'happy', label: 'Happy', emoji: '😊' },
  { id: 'calm', label: 'Calm', emoji: '😌' },
  { id: 'anxious', label: 'Anxious', emoji: '😰' },
  { id: 'sad', label: 'Sad', emoji: '😢' },
  { id: 'irritated', label: 'Irritated', emoji: '😤' },
  { id: 'energetic', label: 'Energetic', emoji: '🤩' }
];

export default function PeriodTracker({ onClose }) {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [periodHistory, setPeriodHistory] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');
  
  const [flowLevel, setFlowLevel] = useState('medium');
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [mood, setMood] = useState('');
  const [notes, setNotes] = useState('');
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');

  // Calculate cycle info
  const cycleInfo = useMemo(() => {
    if (!periodHistory || periodHistory.length === 0) {
      return null;
    }
    
    const lastPeriod = periodHistory[0];
    if (!lastPeriod?.start_date) return null;
    
    const lastStartDate = new Date(lastPeriod.start_date);
    const today = new Date();
    const daysSince = Math.floor((today - lastStartDate) / (1000 * 60 * 60 * 24)) + 1;
    const currentDay = daysSince > 0 ? ((daysSince - 1) % 28) + 1 : 1;
    
    let phase, daysUntilOvulation, daysUntilPeriod;
    
    if (currentDay <= 5) {
      phase = 'MENSTRUAL';
      daysUntilOvulation = 14 - currentDay;
      daysUntilPeriod = 28 - currentDay;
    } else if (currentDay <= 13) {
      phase = 'FOLLICULAR';
      daysUntilOvulation = 14 - currentDay;
      daysUntilPeriod = 28 - currentDay;
    } else if (currentDay <= 16) {
      phase = 'OVULATION';
      daysUntilOvulation = 0;
      daysUntilPeriod = 28 - currentDay;
    } else {
      phase = 'LUTEAL';
      daysUntilOvulation = (28 - currentDay) + 14;
      daysUntilPeriod = 28 - currentDay;
    }
    
    return { phase, currentDay, daysUntilOvulation, daysUntilPeriod, lastStartDate };
  }, [periodHistory]);

  const loadPeriodData = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      
      const { data: historyData, error: historyError } = await supabase
        .from('period_tracking')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false });
      
      if (historyError) throw historyError;
      setPeriodHistory(historyData || []);
      
      const { data: entriesData, error: entriesError } = await supabase
        .from('period_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      
      if (entriesError && entriesError.code !== '42P01') {
        console.error('Entries error:', entriesError);
      }
      setAllEntries(entriesData || []);
      
    } catch (error) {
      console.error('Error loading period data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadPeriodData();
  }, [loadPeriodData]);

  const savePeriodEntry = async () => {
    if (!user?.id || !selectedDate) return;
    
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      const entryData = {
        user_id: user.id,
        date: dateStr,
        flow_level: flowLevel,
        symptoms: selectedSymptoms,
        mood: mood,
        notes: notes
      };
      
      const { error } = await supabase
        .from('period_entries')
        .upsert(entryData, { onConflict: 'user_id,date' });
      
      if (error) throw error;
      
      await loadPeriodData();
      closeModal();
      
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save. Please check if the database table exists.');
    }
  };

  const savePeriodDates = async () => {
    if (!user?.id || !periodStartDate) return;
    
    try {
      const periodData = {
        user_id: user.id,
        start_date: periodStartDate,
        end_date: periodEndDate || null
      };
      
      const { error } = await supabase
        .from('period_tracking')
        .insert(periodData);
      
      if (error) throw error;
      
      await loadPeriodData();
      setShowPeriodForm(false);
      setPeriodStartDate('');
      setPeriodEndDate('');
      
    } catch (error) {
      console.error('Error saving period:', error);
      alert('Failed to save period dates.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDate(null);
    setFlowLevel('medium');
    setSelectedSymptoms([]);
    setMood('');
    setNotes('');
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowModal(true);
    
    const dateStr = date.toISOString().split('T')[0];
    const existingEntry = allEntries.find(e => e.date === dateStr);
    
    if (existingEntry) {
      setFlowLevel(existingEntry.flow_level || 'medium');
      setSelectedSymptoms(existingEntry.symptoms || []);
      setMood(existingEntry.mood || '');
      setNotes(existingEntry.notes || '');
    }
  };

  const toggleSymptom = (symptomId) => {
    setSelectedSymptoms(prev => 
      prev.includes(symptomId) 
        ? prev.filter(s => s !== symptomId)
        : [...prev, symptomId]
    );
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12" />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateStr = date.toISOString().split('T')[0];
      const isToday = date.toDateString() === today.toDateString();
      const entry = allEntries.find(e => e.date === dateStr);
      const isPeriodDay = periodHistory.some(p => {
        const start = new Date(p.start_date);
        const end = p.end_date ? new Date(p.end_date) : new Date(start.getTime() + 4 * 24 * 60 * 60 * 1000);
        return date >= start && date <= end;
      });
      
      days.push(
        <motion.button
          key={day}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleDateClick(date)}
          className={`h-12 w-12 rounded-xl flex flex-col items-center justify-center text-sm font-medium transition-all
            ${isToday ? 'bg-gradient-to-br from-rose-400 to-pink-500 text-white shadow-lg' : ''}
            ${isPeriodDay && !isToday ? 'bg-rose-100 text-rose-700' : ''}
            ${entry && !isToday && !isPeriodDay ? 'bg-purple-50 text-purple-700' : ''}
            ${!isToday && !isPeriodDay && !entry ? 'bg-gray-50 hover:bg-gray-100 text-gray-700' : ''}
          `}
        >
          <span>{day}</span>
          {entry?.flow_level && <span className="text-xs mt-0.5">{FLOW_LEVELS.find(f => f.id === entry.flow_level)?.emoji}</span>}
        </motion.button>
      );
    }
    
    return days;
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg shadow-sm z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">🌸</span> Period Tracker
          </h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-20">
        {/* Cycle Overview */}
        {cycleInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-teal-400 to-cyan-500 rounded-3xl p-6 text-white shadow-xl mb-6"
          >
            <div className="text-center">
              <div className="w-28 h-28 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex flex-col items-center justify-center mb-3">
                <span className="text-3xl">
                  {cycleInfo.phase === 'MENSTRUAL' && '🩸'}
                  {cycleInfo.phase === 'FOLLICULAR' && '🌸'}
                  {cycleInfo.phase === 'OVULATION' && '✨'}
                  {cycleInfo.phase === 'LUTEAL' && '🌙'}
                </span>
                <p className="text-lg font-bold capitalize">{cycleInfo.phase?.toLowerCase()}</p>
                <p className="text-sm opacity-90">Day {cycleInfo.currentDay}</p>
              </div>
              
              {cycleInfo.phase === 'OVULATION' && (
                <p className="text-base font-medium mb-2">💕 Chance to get pregnant</p>
              )}
              
              <div className="flex justify-center gap-6 text-sm">
                <div>
                  <p className="opacity-80">Ovulation in</p>
                  <p className="font-bold text-lg">{cycleInfo.daysUntilOvulation} days</p>
                </div>
                <div className="w-px bg-white/30" />
                <div>
                  <p className="opacity-80">Period in</p>
                  <p className="font-bold text-lg">{cycleInfo.daysUntilPeriod} days</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Add Period Button */}
        {!cycleInfo && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setShowPeriodForm(true)}
            className="w-full bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-2xl p-4 shadow-lg mb-6 flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            Log Your Last Period
          </motion.button>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex-1 py-2 px-4 rounded-xl font-medium transition-all ${activeTab === 'calendar' ? 'bg-white shadow-md text-rose-600' : 'bg-white/50 text-gray-600'}`}
          >
            📅 Calendar
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 px-4 rounded-xl font-medium transition-all ${activeTab === 'history' ? 'bg-white shadow-md text-rose-600' : 'bg-white/50 text-gray-600'}`}
          >
            📊 History
          </button>
        </div>

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-lg p-4"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <div className="w-3 h-3 rounded-full bg-rose-100" />
                Period
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <div className="w-3 h-3 rounded-full bg-purple-50" />
                Logged
              </div>
            </div>
          </motion.div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            {periodHistory.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <span className="text-4xl">📅</span>
                <p className="text-gray-500 mt-2">No period history yet</p>
              </div>
            ) : (
              periodHistory.map((period, idx) => (
                <motion.div
                  key={period.id || idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                      <span className="text-xl">🩸</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {new Date(period.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {period.end_date && ` - ${new Date(period.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                      </p>
                      <p className="text-sm text-gray-500">
                        {period.end_date 
                          ? `${Math.floor((new Date(period.end_date) - new Date(period.start_date)) / (1000 * 60 * 60 * 24)) + 1} days`
                          : 'In progress'
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </div>

      {/* Entry Modal */}
      <AnimatePresence>
        {showModal && selectedDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-t-3xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Flow Level */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Flow Level</label>
                <div className="grid grid-cols-4 gap-2">
                  {FLOW_LEVELS.map(flow => (
                    <button
                      key={flow.id}
                      onClick={() => setFlowLevel(flow.id)}
                      className={`p-3 rounded-xl border-2 transition-all ${flowLevel === flow.id ? 'border-rose-500 bg-rose-50' : 'border-gray-200'}`}
                    >
                      <span className="text-xl">{flow.emoji}</span>
                      <p className="text-xs mt-1">{flow.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Symptoms */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Symptoms</label>
                <div className="flex flex-wrap gap-2">
                  {SYMPTOMS.map(symptom => (
                    <button
                      key={symptom.id}
                      onClick={() => toggleSymptom(symptom.id)}
                      className={`px-3 py-2 rounded-full text-sm border-2 transition-all ${selectedSymptoms.includes(symptom.id) ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
                    >
                      {symptom.emoji} {symptom.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mood */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Mood</label>
                <div className="grid grid-cols-3 gap-2">
                  {MOODS.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMood(m.id)}
                      className={`p-3 rounded-xl border-2 transition-all ${mood === m.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                    >
                      <span className="text-xl">{m.emoji}</span>
                      <p className="text-xs mt-1">{m.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="How are you feeling today?"
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Save Button */}
              <button
                onClick={savePeriodEntry}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl py-3 font-medium shadow-lg"
              >
                Save Entry
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Period Form Modal */}
      <AnimatePresence>
        {showPeriodForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPeriodForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold mb-4">Log Your Period</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={periodStartDate}
                    onChange={e => setPeriodStartDate(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                  <input
                    type="date"
                    value={periodEndDate}
                    onChange={e => setPeriodEndDate(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl"
                  />
                </div>
                
                <button
                  onClick={savePeriodDates}
                  disabled={!periodStartDate}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl py-3 font-medium shadow-lg disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
